import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Label } from "../../../../ui/label";
import { Badge } from "../../../../ui/badge";
import { Separator } from "../../../../ui/separator";
import { Tooltip } from "../../../../ui/tooltip";

import { Plus, Trash2, Info } from "lucide-react";
import { ToolCall, DecisionVariable, DecisionVariablesRegistry } from "../../utils/types";

interface ToolCallModalProps {
  visible: boolean;
  toolCall: ToolCall;
  nodeId?: string;
  isGlobalEdit?: boolean;
  originalSignature?: string;
  globalVariableRegistry?: Record<string, any>;
  decisionVariablesRegistry?: DecisionVariablesRegistry;
  onOk: (nodeId: string, toolCall: ToolCall) => void;
  onCancel: () => void;
}

// Simple tooltip component for modal context
interface SimpleTooltipProps {
  children: React.ReactNode;
  content: string;
}

const SimpleTooltip: React.FC<SimpleTooltipProps> = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
                  <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 px-3 py-2 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-30 pointer-events-none">
          {content}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 border-4 border-transparent border-b-gray-900"></div>
        </div>
      )}
    </div>
  );
};

// Custom combobox component for parameter name selection with autocomplete
interface ParameterNameInputProps {
  value: string;
  onChange: (value: string) => void;  
  placeholder?: string;
  availableOptions: string[];
  className?: string;
}

const ParameterNameInput: React.FC<ParameterNameInputProps> = ({
  value,
  onChange,
  placeholder = "Parameter name",
  availableOptions,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on input value
  useEffect(() => {
    if (value.trim()) {
      const filtered = availableOptions.filter(option =>
        option.toLowerCase().includes(value.toLowerCase()) &&
        option.toLowerCase() !== value.toLowerCase()
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(availableOptions);
    }
    setSelectedIndex(-1); // Reset selection when options change
  }, [value, availableOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
  };

  const handleOptionSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setSelectedIndex(prev => 
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === 'Enter' && selectedIndex >= 0 && isOpen) {
      e.preventDefault();
      handleOptionSelect(filteredOptions[selectedIndex]);
    } else if (e.key === 'Tab' && isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full"
      />
      
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={option}
              className={`px-3 py-2 cursor-pointer text-sm ${
                index === selectedIndex 
                  ? 'bg-accent text-accent-foreground' 
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
              onClick={() => handleOptionSelect(option)}
            >
              <div className="flex items-center justify-between">
                <span className="truncate">{option}</span>
                <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">
                  Available
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom input component with inline variable pills
interface PillInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  conversationVariables: DecisionVariable[];
  placeholderRegistry: Record<string, any>;
}

const PillInput: React.FC<PillInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  className,
  conversationVariables,
  placeholderRegistry,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPills, setShowPills] = useState(false);

  const extractPlaceholders = (text: string): string[] => {
    const matches = text.match(/{([^{}]+)}/g);
    return matches ? matches.map(match => match.slice(1, -1)) : [];
  };

  const placeholders = extractPlaceholders(value);
  const hasPills = placeholders.length > 0;

  // Regular input when no placeholders or when focused for editing
  if (!hasPills || showPills) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setShowPills(true)}
        onBlur={() => setTimeout(() => setShowPills(false), 150)}
        placeholder={placeholder}
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
      />
    );
  }

  // Pill display when has placeholders and not focused
  const segments = [];
  let lastIndex = 0;
  let segmentId = 0;

  const regex = /{([^{}]+)}/g;
  let match;

  while ((match = regex.exec(value)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      segments.push({
        id: segmentId++,
        type: 'text',
        content: value.slice(lastIndex, match.index)
      });
    }

    // Add the placeholder as a pill
    const variableName = match[1];
    const existsInConversationVars = conversationVariables.some(variable => variable.name === variableName);
    const existsInPlaceholders = Object.prototype.hasOwnProperty.call(placeholderRegistry, variableName);
    const exists = existsInConversationVars || existsInPlaceholders;
    const variableType = existsInConversationVars ? "Conversation Variable" : existsInPlaceholders ? "Placeholder" : "Unknown Variable";

    segments.push({
      id: segmentId++,
      type: 'pill',
      content: variableName,
      exists,
      variableType
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < value.length) {
    segments.push({
      id: segmentId++,
      type: 'text',
      content: value.slice(lastIndex)
    });
  }

  return (
    <div 
      className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-text hover:ring-2 hover:ring-ring hover:ring-offset-2 ${className || ''}`}
      onClick={() => {
        setShowPills(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }}
    >
              <div className="flex items-center flex-wrap gap-1 w-full">
        {segments.map((segment) => {
          if (segment.type === 'text') {
            return (
              <span key={segment.id} className="text-foreground">
                {segment.content}
              </span>
            );
          } else {
            return (
              <SimpleTooltip 
                key={segment.id}
                content={segment.exists ? segment.variableType : "Unknown variable - not defined in conversation or global placeholders"}
              >
                <Badge
                  variant={segment.exists ? "default" : "secondary"}
                  className={`text-xs px-1.5 py-0.5 cursor-help ${
                    !segment.exists ? "bg-red-500 text-white hover:bg-red-600" : ""
                  }`}
                >
                  {segment.content}
                </Badge>
              </SimpleTooltip>
            );
          }
        })}
      </div>
      {/* Hidden input for when we need to focus */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setShowPills(true)}
        onBlur={() => setTimeout(() => setShowPills(false), 150)}
        className="absolute opacity-0 pointer-events-none"
        style={{ left: '-9999px' }}
      />
    </div>
  );
};

const ToolCallModal: React.FC<ToolCallModalProps> = ({
  visible,
  toolCall,
  nodeId = "",
  isGlobalEdit = false,
  globalVariableRegistry = {},
  decisionVariablesRegistry = {},
  onOk,
  onCancel
}) => {
  const [parameters, setParameters] = useState<{ id: string; name: string; value: string }[]>([]);
  const [responseVariables, setResponseVariables] = useState<{ id: string; name: string; values: string[] }[]>([]);
  const [editingResponseVariable, setEditingResponseVariable] = useState<string | null>(null);
  const [expandedResponseVariable, setExpandedResponseVariable] = useState<string | null>(null);
  const [newResponseVariableValue, setNewResponseVariableValue] = useState<string>("");
  const [functionName, setFunctionName] = useState<string>("");
  const [functionDescription, setFunctionDescription] = useState<string>("");
  const [errors, setErrors] = useState<{ functionName?: string }>({});

  // Use props directly for real-time updates
  const placeholderRegistry = globalVariableRegistry;
  const conversationVariables = Object.values(decisionVariablesRegistry);

  // Build available parameter names from placeholders and conversation variables
  const availableParameterNames = useMemo(() => {
    const parameterNames = new Set<string>();
    
    // Add placeholder names
    Object.keys(placeholderRegistry).forEach(name => {
      parameterNames.add(name);
    });
    
    // Add conversation variable names
    conversationVariables.forEach(variable => {
      parameterNames.add(variable.name);
    });
    
    // Convert to sorted array for consistent display
    return Array.from(parameterNames).sort();
  }, [placeholderRegistry, conversationVariables]);

  // Extract placeholders from text
  const extractPlaceholders = (text: string): string[] => {
    const regex = /{([^{}]+)}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]);
    }
    return matches;
  };

  // Initialize form values and parameters when modal opens
  useEffect(() => {
    if (visible && toolCall) {
      // Set form values
      setFunctionName(toolCall.name || "");
      setFunctionDescription(toolCall.description || "");
      setErrors({});
      
      // Set parameters with unique IDs
      setParameters((toolCall.parameters || []).map((param, index) => ({
        ...param,
        id: `param_${Date.now()}_${index}`
      })));
      
      // Set response variables with unique IDs
      setResponseVariables((toolCall.responseVariables || []).map((responseVar, index) => ({
        ...responseVar,
        id: `responseVar_${Date.now()}_${index}`
      })));
    }
  }, [visible, toolCall, isGlobalEdit]);

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      setFunctionName("");
      setFunctionDescription("");
      setParameters([]);
      setResponseVariables([]);
      setEditingResponseVariable(null);
      setExpandedResponseVariable(null);
      setNewResponseVariableValue("");
      setErrors({});
    }
  }, [visible]);

  const handleAddParameter = () => {
    setParameters(prev => [...prev, { id: `param_${Date.now()}_${Math.random()}`, name: "", value: "" }]);
  };

  const handleRemoveParameter = (index: number) => {
    setParameters(prev => prev.filter((_, i) => i !== index));
  };

  const handleParameterChange = (index: number, field: keyof { name: string; value: string }, value: string) => {
    setParameters(prev => prev.map((param, i) => 
      i === index ? { ...param, [field]: value } : param
    ));
  };

  const handleAddResponseVariable = () => {
    const newId = `responseVar_${Date.now()}_${Math.random()}`;
    setResponseVariables(prev => [...prev, { id: newId, name: "", values: [] }]);
    setExpandedResponseVariable(newId);
  };

  const handleRemoveResponseVariable = (index: number) => {
    const responseVarToRemove = responseVariables[index];
    setResponseVariables(prev => prev.filter((_, i) => i !== index));
    
    // Clear expanded state if we're removing the expanded variable
    if (expandedResponseVariable === responseVarToRemove?.id) {
      setExpandedResponseVariable(null);
    }
    
    // Clear editing state if we're removing the variable being edited
    if (editingResponseVariable === responseVarToRemove?.id) {
      setEditingResponseVariable(null);
      setNewResponseVariableValue("");
    }
  };

  const handleResponseVariableChange = (index: number, field: keyof { name: string; values: string[] }, value: string | string[]) => {
    setResponseVariables(prev => prev.map((responseVar, i) => 
      i === index ? { ...responseVar, [field]: value } : responseVar
    ));
  };

  const handleAddResponseVariableValue = (responseVarIndex: number, value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;
    
    const currentValues = responseVariables[responseVarIndex]?.values || [];
    
    // Check if value already exists to prevent duplicates
    if (currentValues.includes(trimmedValue)) {
      setNewResponseVariableValue("");
      return;
    }
    
    const updatedValues = [...currentValues, trimmedValue];
    handleResponseVariableChange(responseVarIndex, 'values', updatedValues);
    setNewResponseVariableValue("");
  };

  const handleRemoveResponseVariableValue = (responseVarIndex: number, valueIndex: number) => {
    const currentValues = responseVariables[responseVarIndex]?.values || [];
    const updatedValues = currentValues.filter((_, index) => index !== valueIndex);
    handleResponseVariableChange(responseVarIndex, 'values', updatedValues);
  };

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: { functionName?: string } = {};
    
    if (!functionName.trim()) {
      newErrors.functionName = "Please enter a function name";
    } else if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(functionName.trim())) {
      newErrors.functionName = "Function name must be a valid identifier";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOk = () => {
    if (!validateForm()) {
      return;
    }
    
    const updatedToolCall: ToolCall = {
      name: functionName.trim(),
      description: functionDescription.trim() || "",
      parameters: parameters.filter(p => p.name.trim() && p.value.trim()).map(p => ({ name: p.name.trim(), value: p.value.trim() })),
      responseVariables: responseVariables.filter(rv => rv.name.trim() && rv.values.length > 0).map(rv => ({ name: rv.name.trim(), values: rv.values }))
    };
    
    onOk(nodeId, updatedToolCall);
  };

  // Validation function to determine if OK button should be disabled
  const isOkDisabled = (): boolean => {
    const trimmedName = functionName.trim();
    
    // Only require a valid function name - parameters are optional
    return !trimmedName || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName);
  };

  const title = isGlobalEdit 
    ? "Edit Tool Call (Global Update)" 
    : "Edit Tool Call (Single Node)";
    
  const description = isGlobalEdit
    ? "This will update ALL nodes that use this tool call"
    : "This will only update this specific node";

  return (
    <Dialog open={visible} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div>
            <Badge 
              variant={isGlobalEdit ? "destructive" : "default"}
              className="text-xs"
            >
              {description}
            </Badge>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="function-name" className="text-sm font-gc-med text-foreground">
                Function Name
              </Label>
              <Input
                id="function-name"
                placeholder="e.g., get_weather"
                value={functionName}
                onChange={(e) => setFunctionName(e.target.value)}
                className={errors.functionName ? "border-red-500" : ""}
              />
              {errors.functionName && (
                <p className="text-xs text-red-600">{errors.functionName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-gc-med text-foreground">
                Description
              </Label>
              <Textarea
                id="description"
                rows={2}
                placeholder="Describe what this function does"
                value={functionDescription}
                onChange={(e) => setFunctionDescription(e.target.value)}
              />
            </div>

            <Separator />
            <div className="pt-4">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-sm font-gc-med text-foreground">Parameters</h4>
                <Tooltip content="Input values required by the function to work properly" side="right" immediate>
                  <Info className="w-4 h-4 text-maitai-green cursor-help" />
                </Tooltip>
              </div>

              <div className="mb-4 max-h-40 overflow-y-auto overflow-x-hidden p-1">
                {parameters.map((param, index) => {
                  // Check if parameter name matches a conversation variable or placeholder
                  const matchingVariable = conversationVariables.find(
                    variable => variable.name.toLowerCase() === param.name.toLowerCase()
                  );
                  const matchingPlaceholder = Object.keys(placeholderRegistry).find(
                    placeholder => placeholder.toLowerCase() === param.name.toLowerCase()
                  );
                  
                  const matchingItem = matchingVariable || matchingPlaceholder;
                  const matchingName = matchingVariable?.name || matchingPlaceholder;
                  
                  const valueInputPlaceholder = matchingItem
                    ? `Press Tab to use: {${matchingName}}`
                    : "Parameter value";

                  return (
                    <div key={param.id} className="mb-4">
                      {/* Parameter Input Row */}
                      <div className="flex gap-3 mb-2">
                        <ParameterNameInput
                          placeholder="Parameter name"
                          value={param.name}
                          onChange={(value) => handleParameterChange(index, "name", value)}
                          availableOptions={availableParameterNames}
                          className="flex-[40]"
                        />
                        <PillInput
                          placeholder={valueInputPlaceholder}
                          value={param.value}
                          onChange={(value) => handleParameterChange(index, "value", value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Tab" &&
                              matchingName &&
                              !param.value
                            ) {
                              e.preventDefault();
                              handleParameterChange(index, "value", `{${matchingName}}`);
                            }
                          }}
                          className="flex-[45]"
                          conversationVariables={conversationVariables}
                          placeholderRegistry={placeholderRegistry}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveParameter(index)}
                          className="px-2 border-red-600 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                    </div>
                  );
                })}
                
                <Button
                  variant="outline"
                  onClick={handleAddParameter}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Parameter
                </Button>
              </div>

              <Separator />
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <h4 className="text-sm font-gc-med text-foreground">Response Variables Expected</h4>
                  <Tooltip content="Data the function will return that you can use in your conversation" side="right" immediate>
                    <Info className="w-4 h-4 text-maitai-green cursor-help" />
                  </Tooltip>
                </div>

                <div 
                  className="mb-4 max-h-40 overflow-y-auto overflow-x-hidden p-1"
                  onClick={(e) => {
                    // Collapse expanded variable when clicking in empty space
                    if (e.target === e.currentTarget) {
                      setExpandedResponseVariable(null);
                    }
                  }}
                >
                  {responseVariables.map((responseVar, index) => {
                    const isExpanded = expandedResponseVariable === responseVar.id;
                    const hasValues = responseVar.values && responseVar.values.length > 0;
                    
                    return (
                      <div key={responseVar.id} className="mb-2">
                        {/* Compact Response Variable Input Row */}
                        <div className="flex gap-2 items-center">
                          <Input
                            placeholder="Variable name"
                            value={responseVar.name}
                            onChange={(e) => handleResponseVariableChange(index, "name", e.target.value)}
                            onFocus={() => setExpandedResponseVariable(responseVar.id)}
                            className="flex-[25]"
                          />
                          <Input 
                            value={editingResponseVariable === responseVar.id ? newResponseVariableValue : ""}
                            onChange={(e) => {
                              setEditingResponseVariable(responseVar.id);
                              setExpandedResponseVariable(responseVar.id);
                              setNewResponseVariableValue(e.target.value);
                            }}
                            onFocus={() => setExpandedResponseVariable(responseVar.id)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddResponseVariableValue(index, newResponseVariableValue)}
                            placeholder="Add possible value (Enter or + to add multiple)"
                            className="flex-[40]"
                          />
                          {hasValues && !isExpanded && (
                            <div className="flex-[20] text-xs text-muted-foreground px-2">
                              {responseVar.values.length} value{responseVar.values.length !== 1 ? 's' : ''}
                            </div>
                          )}
                          {!hasValues && !isExpanded && (
                            <div className="flex-[20]" />
                          )}
                          <Button 
                            onClick={() => handleAddResponseVariableValue(index, newResponseVariableValue)}
                            disabled={!newResponseVariableValue.trim() || editingResponseVariable !== responseVar.id}
                            size="sm"
                            variant="outline"
                            className="px-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveResponseVariable(index)}
                            className="px-2 border-red-600 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Expandable Value Pills - Only show for expanded variable */}
                        {isExpanded && (
                          <div className="mt-2 ml-2">
                            {responseVar.values?.length === 0 ? (
                              <p className="text-xs text-muted-foreground">
                                No values yet. Add some possible values for this response variable.
                              </p>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {responseVar.values?.map((value, valueIndex) => (
                                  <Badge 
                                    key={`${value}-${valueIndex}`}
                                    variant="outline"
                                    className="text-xs border-maitai-green/30 text-maitai-green bg-maitai-green/10 hover:bg-maitai-green/20 transition-colors"
                                  >
                                    {value}
                                    <button
                                      onClick={() => handleRemoveResponseVariableValue(index, valueIndex)}
                                      className="ml-1 hover:text-destructive transition-colors"
                                    >
                                      ×
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    onClick={handleAddResponseVariable}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Response Variable
                  </Button>
                </div>
              </div>

            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleOk} disabled={isOkDisabled()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ToolCallModal; 